/**
 * DealAnalysisTab - ESA vs CapEx Deal Modeling
 *
 * Replaces the "Financials" sub-tab in the Results section.
 * Allows developers to model ESA (Energy Services Agreement) vs CapEx sale deals
 * and see real-time calculations of developer returns and customer value.
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Divider,
    Chip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    InputAdornment,
    Checkbox,
    FormControlLabel,
    Alert,
    Tooltip,
    IconButton,
    Switch,
} from '@mui/material';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    ReferenceLine,
    Cell,
} from 'recharts';
import {
    ExpandMore,
    TrendingUp,
    AttachMoney,
    Compare,
    Download,
    Info,
    Handshake,
    Store,
    AccountBalance,
    Shield as ShieldIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useThemeMode, vortxColors, formatCurrency, formatNumber, vortxTypography } from '@vortx/shared-ui';
import { RETRO_COLORS } from './retro/RetroFormComponents';
import { FinancialCalculator } from '../FinancialAnalysis';
import BudgetTab from './BudgetTab';
import GuaranteeScoreCard from './guarantee/GuaranteeScoreCard';
import GuaranteeSimulationSection from './guarantee/GuaranteeSimulationSection';
import { useQuickGuaranteeScore } from '../hooks/useQuickGuaranteeScore';

// Helper to download a file
function downloadFile(content, filename, mimeType = 'text/csv') {
    const blob = new Blob([content], { type: mimeType });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
}

// Helper to escape CSV values (handle commas, quotes, newlines)
function escapeCSV(value) {
    if (value === null || value === undefined) return '';
    const str = String(value);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
}

// Generate CSV content for deal analysis export
function generateDealAnalysisCSV(viewMode, esaConfig, capexConfig, esaResults, capexResults, budget, systemSizes) {
    const rows = [];
    const timestamp = new Date().toISOString().split('T')[0];

    // Header
    rows.push(['Deal Analysis Export']);
    rows.push(['Generated', timestamp]);
    rows.push(['View Mode', viewMode]);
    rows.push([]);

    // System configuration
    rows.push(['SYSTEM CONFIGURATION']);
    rows.push(['Solar PV (kW)', systemSizes?.pvKw || 0]);
    rows.push(['Battery (kWh)', systemSizes?.batteryKwh || 0]);
    if (systemSizes?.batteryKw) {
        rows.push(['Battery Power (kW)', systemSizes.batteryKw]);
    }
    if (systemSizes?.generatorKw) {
        rows.push(['Generator (kW)', systemSizes.generatorKw]);
    }
    rows.push(['Total CapEx ($)', budget?.capexCalculations?.total || 0]);
    rows.push(['ITC Amount ($)', budget?.itcCalculations?.totalItc || 0]);
    rows.push(['Net Cost After ITC ($)', budget?.itcCalculations?.netCostAfterItc || budget?.capexCalculations?.total || 0]);
    rows.push([]);

    // ESA Configuration and Results (if applicable)
    if (viewMode === 'esa' || viewMode === 'compare') {
        rows.push(['ESA CONFIGURATION']);
        rows.push(['Pricing Model', esaConfig.model]);
        rows.push(['Term (years)', esaConfig.term]);
        rows.push(['Annual Escalation (%)', (esaConfig.escalation * 100).toFixed(2)]);

        // Model-specific parameters
        if (esaConfig.model === 'fixed_rate') {
            rows.push(['Rate ($/kWh)', esaConfig.rate]);
        } else if (esaConfig.model === 'savings_share') {
            rows.push(['Savings Share (%)', (esaConfig.sharePercent * 100).toFixed(0)]);
        } else if (esaConfig.model === 'fixed_monthly') {
            rows.push(['Monthly Payment ($)', esaConfig.payment]);
        } else if (esaConfig.model === 'capacity_payment') {
            rows.push(['Capacity Rate ($/kW/mo)', esaConfig.ratePerKw]);
        }
        rows.push([]);

        // ESA Results
        if (esaResults) {
            rows.push(['ESA DEVELOPER RETURNS']);
            rows.push(['IRR (with ITC) (%)', esaResults.developerReturns?.withItc?.irr ? (esaResults.developerReturns.withItc.irr * 100).toFixed(2) : 'N/A']);
            rows.push(['NPV (with ITC) ($)', esaResults.developerReturns?.withItc?.npv ?? 'N/A']);
            rows.push(['Payback Period (years)', esaResults.developerReturns?.withItc?.paybackYears?.toFixed(1) ?? 'N/A']);
            rows.push(['Total Revenue ($)', esaResults.revenue?.totalRevenue ?? 'N/A']);
            rows.push(['Total Expenses ($)', esaResults.expenses?.totalExpenses ?? 'N/A']);
            rows.push([]);

            rows.push(['ESA CUSTOMER VALUE']);
            rows.push(['Year 1 Savings ($)', esaResults.customerValue?.year1Savings ?? 'N/A']);
            rows.push(['Lifetime Savings ($)', esaResults.customerValue?.lifetimeSavings ?? 'N/A']);
            rows.push(['NPV of Savings ($)', esaResults.customerValue?.npvOfSavings ?? 'N/A']);
            rows.push(['Effective Rate ($/kWh)', esaResults.customerValue?.effectiveRate?.toFixed(4) ?? 'N/A']);
            rows.push(['Rate Discount (%)', esaResults.customerValue?.rateDiscount ? (esaResults.customerValue.rateDiscount * 100).toFixed(1) : 'N/A']);
            rows.push([]);

            // Annual Cash Flows
            if (esaResults.annualCashFlows && esaResults.revenue?.annual) {
                rows.push(['ANNUAL CASH FLOWS']);
                rows.push(['Year', 'Revenue ($)', 'Expenses ($)', 'Net Cash Flow ($)', 'Cumulative ($)']);

                const systemCost = budget?.capexCalculations?.total || 0;
                const itcAmount = budget?.itcCalculations?.totalItc || 0;
                let cumulative = -systemCost + itcAmount;

                esaResults.revenue.annual.forEach((rev, idx) => {
                    const exp = esaResults.expenses?.annual?.[idx];
                    const netCashFlow = esaResults.annualCashFlows[idx] || 0;
                    cumulative += netCashFlow;
                    rows.push([
                        rev.year,
                        rev.revenue?.toFixed(2) ?? 0,
                        exp?.total?.toFixed(2) ?? 0,
                        netCashFlow.toFixed(2),
                        cumulative.toFixed(2)
                    ]);
                });
                rows.push([]);
            }
        }
    }

    // CapEx Configuration and Results (if applicable)
    if (viewMode === 'capex' || viewMode === 'compare') {
        rows.push(['CAPEX SALE CONFIGURATION']);
        rows.push(['Markup (%)', capexConfig.markupPercent]);
        rows.push(['Customer ITC Eligible', capexConfig.customerItcEligible ? 'Yes' : 'No']);
        rows.push([]);

        if (capexResults) {
            rows.push(['CAPEX DEVELOPER RETURNS']);
            rows.push(['Sale Price ($)', capexResults.salePrice?.toFixed(2) ?? 'N/A']);
            rows.push(['Gross Margin ($)', capexResults.markup?.grossMargin?.toFixed(2) ?? 'N/A']);
            rows.push(['Gross Margin (%)', capexResults.markup?.grossMarginPercent ? (capexResults.markup.grossMarginPercent * 100).toFixed(1) : 'N/A']);
            rows.push(['Developer ITC ($)', capexResults.markup?.developerItc?.toFixed(2) ?? 'N/A']);
            rows.push(['Net Margin (with ITC) ($)', capexResults.markup?.netMargin?.toFixed(2) ?? 'N/A']);
            rows.push([]);

            rows.push(['CAPEX CUSTOMER VALUE']);
            rows.push(['Customer ITC ($)', capexResults.customerItc?.toFixed(2) ?? 'N/A']);
            rows.push(['Payback Period (years)', capexResults.customerPayback?.toFixed(1) ?? 'N/A']);
            rows.push(['Customer NPV ($)', capexResults.customerNpv?.toFixed(2) ?? 'N/A']);
            rows.push(['Lifetime Savings ($)', capexResults.lifetimeSavings?.toFixed(2) ?? 'N/A']);
            if (capexResults.lifetimeVpp > 0) {
                rows.push(['Lifetime VPP Revenue ($)', capexResults.lifetimeVpp?.toFixed(2) ?? 'N/A']);
            }
            rows.push(['Lifetime O&M ($)', capexResults.lifetimeOM?.toFixed(2) ?? 'N/A']);
            rows.push(['Net Lifetime Value ($)', capexResults.lifetimeNetValue?.toFixed(2) ?? 'N/A']);
            rows.push(['Customer IRR (%)', capexResults.customerIrr != null ? (capexResults.customerIrr * 100).toFixed(1) : 'N/A']);
            rows.push([]);

            // CapEx annual cash flows
            if (capexResults.annualCashFlows) {
                rows.push(['CAPEX ANNUAL CASH FLOWS']);
                rows.push(['Year', 'Savings ($)', 'O&M ($)', 'Net Cash Flow ($)', 'Cumulative ($)']);
                rows.push([0, '', '', capexResults.year0CashFlow?.toFixed(2), capexResults.year0CashFlow?.toFixed(2)]);
                capexResults.annualCashFlows.forEach(cf => {
                    rows.push([cf.year, cf.savings?.toFixed(2), cf.om?.toFixed(2), cf.netCashFlow?.toFixed(2), cf.cumulative?.toFixed(2)]);
                });
                rows.push([]);
            }
        }
    }

    // Convert rows to CSV string with proper escaping
    return rows.map(row => row.map(escapeCSV).join(',')).join('\n');
}

// Default operating cost assumptions
const DEFAULT_OM_PER_KW_YEAR = 15;
const DEFAULT_INSURANCE_PERCENT = 0.005;
const DEFAULT_ASSET_MGMT_PER_YEAR = 2400;
const DEFAULT_ITC_ELIGIBLE_FRACTION = 0.9;
const ITC_RATE = 0.30;

/**
 * Format percentage with specified decimals.
 *
 * IMPORTANT: All values should be passed as decimals (0-1 range).
 * For example: 0.25 for 25%, 0.085 for 8.5%
 * The function will multiply by 100 for display.
 *
 * @param {number} value - The decimal value (e.g., 0.25 for 25%)
 * @param {number} decimals - Number of decimal places to show (default: 1)
 * @returns {string} Formatted percentage string (e.g., "25.0%")
 */
const formatPercent = (value, decimals = 1) => {
    if (value === null || value === undefined || isNaN(value)) return '-';
    // All values should be decimals (0-1 range) - multiply by 100 for display
    const pctValue = value * 100;
    return `${pctValue.toFixed(decimals)}%`;
};

// ESA Pricing Model Labels
const PRICING_MODEL_LABELS = {
    fixed_rate: 'Fixed Rate ($/kWh)',
    savings_share: 'Savings Share (%)',
    fixed_monthly: 'Fixed Fee ($/year)',
    capacity_payment: 'Capacity Payment ($/kW)'
};

// Default ESA Configuration
const DEFAULT_ESA_CONFIG = {
    model: 'fixed_rate',
    term: 20,
    escalation: 0.025,
    // Model-specific parameters
    rate: 0.12,           // fixed_rate: $/kWh
    sharePercent: 0.80,   // savings_share: decimal
    payment: 3500,        // fixed_monthly: $/month
    ratePerKw: 12         // capacity_payment: $/kW/month
};

// Default CapEx Configuration
const DEFAULT_CAPEX_CONFIG = {
    markupPercent: 20,
    customerItcEligible: true,
    escalationRate: 0.03,
    term: 20
};

/**
 * DealAnalysisTab Props:
 * @param {Object} budget - Budget state from useBudget hook
 * @param {Object} systemSizes - { pvKw, batteryKw, batteryKwh, generatorKw }
 * @param {number} annualProductionKwh - Annual solar production in kWh
 * @param {number} annualSavings - Annual utility bill savings in $
 * @param {number} bauUtilityRate - BAU utility rate in $/kWh (from financial analysis)
 * @param {Object} operatingCosts - OpEx configuration from budget
 * @param {Function} onConfigChange - Callback when configuration changes (for auto-save)
 * @param {Object} initialConfig - Initial configuration to restore from saved scenario
 * @param {Object} budgetTabProps - Props to pass through to embedded BudgetTab
 * @param {Object} monthlyPerformanceData - Monthly performance data with DER charges, fuel costs
 * @param {Object} financialAnalysis - Comprehensive financial analysis from FinancialCalculator (for consistent NPV/payback)
 */
function DealAnalysisTab({
    budget,
    systemSizes = {},
    annualProductionKwh = 0,
    annualSavings = 0,
    bauUtilityRate = 0,
    operatingCosts = {},
    onConfigChange,
    initialConfig = null,
    budgetTabProps = {},
    monthlyPerformanceData = null,
    financialAnalysis = null,
    vppEstimate = null,
    projectId = null,
    scenarioId = null,
    optimization = null,
}) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const { isRetro } = useThemeMode();

    // Retro style constants
    const RETRO_CARD_SX = {
        borderRadius: 0,
        border: `1px solid ${RETRO_COLORS.teal}`,
        backgroundColor: 'transparent',
    };
    const RETRO_FONT_SX = { fontFamily: vortxTypography.mono };

    // Quick guarantee score — fires when optimization results exist
    const hasOptResults = Boolean(optimization?.results?.outputs);
    const { scoreData: guaranteeScore, loading: guaranteeLoading } = useQuickGuaranteeScore(
        projectId, scenarioId, hasOptResults
    );

    // View mode: 'esa', 'capex', or 'compare'
    const [viewMode, setViewMode] = useState(initialConfig?.viewMode || 'esa');

    // Budget section expanded state
    const [budgetExpanded, setBudgetExpanded] = useState(false);

    // Cash flows table expanded state
    const [cashFlowsExpanded, setCashFlowsExpanded] = useState(false);

    // ESA Configuration state
    const [esaConfig, setEsaConfig] = useState(initialConfig?.esaConfig || DEFAULT_ESA_CONFIG);

    // Check if $/kWh pricing model is available (requires production data)
    const hasProductionData = annualProductionKwh > 0;

    // Auto-switch away from fixed_rate if no production data (default to fixed_monthly)
    React.useEffect(() => {
        if (!hasProductionData && esaConfig.model === 'fixed_rate') {
            console.log('⚠️ [DealAnalysis] No production data - switching from fixed_rate to fixed_monthly');
            setEsaConfig(prev => ({ ...prev, model: 'fixed_monthly' }));
        }
    }, [hasProductionData, esaConfig.model]);

    // CapEx Configuration state
    const [capexConfig, setCapexConfig] = useState({ ...DEFAULT_CAPEX_CONFIG, ...initialConfig?.capexConfig });

    // Discount rate for NPV calculations
    const [discountRate, setDiscountRate] = useState(initialConfig?.discountRate || 0.08);

    // VPP Revenue integration
    const [includeVpp, setIncludeVpp] = useState(initialConfig?.vppIncluded || false);
    const [vppDurationYears, setVppDurationYears] = useState(initialConfig?.vppDurationYears || 10);

    // Computed VPP annual revenue (midpoint of adjusted range)
    const vppAnnualRevenue = useMemo(() => {
        if (!vppEstimate || !includeVpp) return 0;
        const low = vppEstimate.adjTotalAnnualLow || 0;
        const high = vppEstimate.adjTotalAnnualHigh || 0;
        return (low + high) / 2;
    }, [vppEstimate, includeVpp]);

    // Lifetime VPP revenue (escalated at 3% energy rate, capped at VPP duration)
    const esaLifetimeVpp = useMemo(() => {
        if (vppAnnualRevenue <= 0) return 0;
        const years = Math.min(vppDurationYears, 99);
        return Array.from({ length: years }, (_, i) =>
            vppAnnualRevenue * Math.pow(1.03, i)
        ).reduce((sum, v) => sum + v, 0);
    }, [vppAnnualRevenue, vppDurationYears]);

    // Extract values from budget hook
    const {
        capexCalculations = {},
        itcCalculations = {},
        opexCalculations = {},
        loading: budgetLoading = false
    } = budget || {};

    // Total system cost (from budget) - only use values when budget is loaded
    // IMPORTANT: Don't show fallback values until actual data is loaded
    const systemCost = budgetLoading ? null : (capexCalculations?.total || 0);
    const itcAmount = budgetLoading ? null : (itcCalculations?.totalItc || 0);
    const netCostAfterItc = budgetLoading ? null : (itcCalculations?.netCostAfterItc || systemCost);

    // Total capacity for capacity payment model - includes all DER assets (PV, battery, generator)
    const totalCapacityKw = (systemSizes?.pvKw || 0) + (systemSizes?.batteryKw || 0) + (systemSizes?.generatorKw || 0);

    // Build ESA pricing object for calculator
    const esaPricingForCalculator = useMemo(() => {
        const base = {
            model: esaConfig.model,
            term: esaConfig.term,
            escalation: esaConfig.escalation
        };

        switch (esaConfig.model) {
            case 'fixed_rate':
                return { ...base, rate: esaConfig.rate };
            case 'savings_share':
                return { ...base, sharePercent: esaConfig.sharePercent };
            case 'fixed_monthly':
                return { ...base, payment: esaConfig.payment };
            case 'capacity_payment':
                return { ...base, ratePerKw: esaConfig.ratePerKw };
            default:
                return base;
        }
    }, [esaConfig]);

    // Calculate ESA results
    const esaResults = useMemo(() => {
        if (!systemCost || systemCost <= 0) return null;

        try {
            // 1. Calculate ESA Revenue
            const esaRevenue = FinancialCalculator.calculateEsaRevenue(
                esaPricingForCalculator,
                annualProductionKwh,
                annualSavings,
                totalCapacityKw
            );

            // 2. Calculate ESA Expenses
            const esaExpenses = FinancialCalculator.calculateEsaExpenses(
                {
                    pvKw: systemSizes?.pvKw || 0,
                    batteryKwh: systemSizes?.batteryKwh || 0,
                    generatorKw: systemSizes?.generatorKw || 0
                },
                {
                    omPerKwYear: opexCalculations?.pvOM ? opexCalculations.pvOM / (systemSizes?.pvKw || 1) : DEFAULT_OM_PER_KW_YEAR,
                    omPerKwhYear: opexCalculations?.batteryOM ? opexCalculations.batteryOM / (systemSizes?.batteryKwh || 1) : 5,
                    omPerKwGenYear: opexCalculations?.generatorOM ? opexCalculations.generatorOM / (systemSizes?.generatorKw || 1) : 20,
                    insurancePercent: DEFAULT_INSURANCE_PERCENT,
                    assetMgmtPerYear: DEFAULT_ASSET_MGMT_PER_YEAR
                },
                systemCost,
                esaConfig.term
            );

            // 3. Calculate annual net cash flows (ESA revenue + VPP revenue - expenses)
            const energyEsc = 0.03;
            const annualCashFlows = esaRevenue.annual.map((rev, i) => {
                const exp = esaExpenses.annual[i];
                const year = i + 1;
                // VPP revenue: only for years within duration, escalated at energy rate
                let vppRev = 0;
                if (vppAnnualRevenue > 0 && year <= vppDurationYears) {
                    vppRev = vppAnnualRevenue * Math.pow(1 + energyEsc, i);
                }
                return rev.revenue + vppRev - exp.total;
            });

            // 4. Developer Returns
            const developerReturns = FinancialCalculator.calculateDeveloperReturns(
                systemCost,
                annualCashFlows,
                itcAmount,
                discountRate
            );

            // 5. Customer Value
            // Create arrays for customer value calculation with energy escalation (default 3%)
            const energyEscalation = 0.03; // Energy cost escalation rate
            const esaPaymentsArray = esaRevenue.annual.map(r => r.revenue);

            // Calculate escalated utility savings for each year
            // Use annualSavings as Year 1 baseline, escalate by energy rate
            const annualUtilitySavingsArray = Array(esaConfig.term).fill(0).map((_, i) => {
                return annualSavings * Math.pow(1 + energyEscalation, i);
            });

            // Extract Year 1 DER charges (standby + departing load) from monthly performance data
            let year1DerCharges = 0;
            let year1StandbyCharges = 0;
            let year1DepartingLoadCharges = 0;
            if (monthlyPerformanceData?.monthly_der_charges?.length > 0) {
                year1StandbyCharges = monthlyPerformanceData.monthly_der_charges.reduce(
                    (sum, m) => sum + (m.standby_cost || 0), 0
                );
                year1DepartingLoadCharges = monthlyPerformanceData.monthly_der_charges.reduce(
                    (sum, m) => sum + (m.departing_load_cost || 0), 0
                );
                year1DerCharges = year1StandbyCharges + year1DepartingLoadCharges;
            }

            // Extract Year 1 fuel costs from monthly asset performance
            let year1FuelCost = 0;
            if (monthlyPerformanceData?.asset_performance?.length > 0) {
                year1FuelCost = monthlyPerformanceData.asset_performance.reduce(
                    (sum, m) => sum + (m.generator_fuel_cost || 0), 0
                );
            }

            // Calculate customer annual cashflows (Savings - ESA Payment - DER Charges - Fuel Costs)
            // DER charges and fuel costs escalate with energy rate
            const customerAnnualCashflows = annualUtilitySavingsArray.map((savings, i) => {
                const escalatedDerCharges = year1DerCharges * Math.pow(1 + energyEscalation, i);
                const escalatedFuelCost = year1FuelCost * Math.pow(1 + energyEscalation, i);
                const esaPayment = esaPaymentsArray[i] || 0;
                const netCashflow = savings - esaPayment - escalatedDerCharges - escalatedFuelCost;

                return {
                    year: i + 1,
                    utilitySavings: savings,
                    esaPayment,
                    derCharges: escalatedDerCharges,
                    fuelCost: escalatedFuelCost,
                    netCashflow
                };
            });

            // Calculate effective rate based on pricing model
            // For fixed_rate: use the configured rate
            // For other models: calculate from revenue/production if production exists
            let effectiveRate = 0;
            if (esaConfig.model === 'fixed_rate') {
                effectiveRate = esaConfig.rate;
            } else if (annualProductionKwh > 0) {
                effectiveRate = esaPaymentsArray[0] / annualProductionKwh;
            }
            // For capacity_payment without production, effectiveRate stays 0 (not applicable)

            // Calculate customer value metrics from customerAnnualCashflows (includes all costs)
            const year1Savings = customerAnnualCashflows[0]?.netCashflow || 0;
            const lifetimeSavings = customerAnnualCashflows.reduce((sum, cf) => sum + cf.netCashflow, 0);

            // Calculate NPV of customer savings using discount rate
            const npvOfSavings = customerAnnualCashflows.reduce((npv, cf, i) => {
                return npv + cf.netCashflow / Math.pow(1 + discountRate, i + 1);
            }, 0);

            // Calculate rate discount (comparing ESA effective rate to BAU utility rate)
            const rateDiscount = bauUtilityRate > 0 && effectiveRate > 0
                ? (bauUtilityRate - effectiveRate) / bauUtilityRate
                : 0;

            return {
                revenue: esaRevenue,
                expenses: esaExpenses,
                annualCashFlows,
                developerReturns,
                customerAnnualCashflows,
                customerValue: {
                    year1Savings,
                    lifetimeSavings,
                    npvOfSavings,
                    rateDiscount,
                    effectiveRate,
                    pricingModel: esaConfig.model
                }
            };
        } catch (error) {
            console.error('ESA calculation error:', error);
            return null;
        }
    }, [
        esaPricingForCalculator,
        systemCost,
        annualProductionKwh,
        annualSavings,
        totalCapacityKw,
        systemSizes,
        opexCalculations,
        esaConfig.term,
        itcAmount,
        discountRate,
        bauUtilityRate,
        monthlyPerformanceData,
        vppAnnualRevenue,
        vppDurationYears,
    ]);

    // Calculate CapEx results with year-by-year cash flows
    const capexResults = useMemo(() => {
        if (!systemCost || systemCost <= 0) return null;

        try {
            // Sale price based on markup
            const salePrice = systemCost * (1 + capexConfig.markupPercent / 100);

            // Internal costs for margin calculation
            const internalCosts = {
                totalInternalCost: systemCost,
                itcEligibleCost: itcCalculations?.totalUpfront || systemCost,
                itcRate: (itcCalculations?.effectiveItcRate || 30) / 100
            };

            // Customer ITC eligible amount (if customer is eligible)
            const customerItcEligibleAmount = capexConfig.customerItcEligible ? salePrice * DEFAULT_ITC_ELIGIBLE_FRACTION : 0;

            // CapEx Margin calculation
            const capexMargin = FinancialCalculator.calculateCapexMargin(
                internalCosts,
                salePrice,
                customerItcEligibleAmount
            );

            const customerItc = capexConfig.customerItcEligible ? customerItcEligibleAmount * ITC_RATE : 0;
            const term = capexConfig.term || 25;
            const escalation = capexConfig.escalationRate || 0;

            // Annual O&M from budget operating costs
            const pvOM = operatingCosts?.pvOM || 0;
            const batteryOM = operatingCosts?.batteryOM || 0;
            const generatorOM = operatingCosts?.generatorOM || 0;
            const insurance = operatingCosts?.insurance || 0;
            const annualOM = pvOM + batteryOM + generatorOM + insurance;
            const omEscalation = 0.02; // 2% O&M escalation

            // Extract Year 1 DER charges and fuel costs from monthly performance data
            let capexYear1DerCharges = 0;
            let capexYear1FuelCost = 0;
            if (monthlyPerformanceData?.monthly_der_charges?.length > 0) {
                capexYear1DerCharges = monthlyPerformanceData.monthly_der_charges.reduce(
                    (sum, m) => sum + (m.standby_cost || 0) + (m.departing_load_cost || 0), 0
                );
            }
            if (monthlyPerformanceData?.asset_performance?.length > 0) {
                capexYear1FuelCost = monthlyPerformanceData.asset_performance.reduce(
                    (sum, m) => sum + (m.generator_fuel_cost || 0), 0
                );
            }

            // Year 0: purchase
            const year0CashFlow = -salePrice + customerItc;

            // Year-by-year cash flows
            let cumulative = year0CashFlow;
            let customerPayback = null;
            let lifetimeSavings = 0;
            let lifetimeVpp = 0;
            let lifetimeOM = 0;
            let customerNpvSum = year0CashFlow; // start with year 0

            const annualCashFlows = [];
            for (let yr = 1; yr <= term; yr++) {
                // Year 1 = base values (no escalation), escalation starts Year 2
                const savings = annualSavings * Math.pow(1 + escalation, yr - 1);
                const om = annualOM * Math.pow(1 + omEscalation, yr - 1);
                const derCharges = capexYear1DerCharges * Math.pow(1 + escalation, yr - 1);
                const fuelCost = capexYear1FuelCost * Math.pow(1 + escalation, yr - 1);
                // VPP revenue: only for years within duration, escalated at energy rate
                let vppRev = 0;
                if (vppAnnualRevenue > 0 && yr <= vppDurationYears) {
                    vppRev = vppAnnualRevenue * Math.pow(1 + escalation, yr - 1);
                }
                const netCashFlow = savings + vppRev - om - derCharges - fuelCost;
                cumulative += netCashFlow;
                lifetimeSavings += savings;
                lifetimeVpp += vppRev;
                lifetimeOM += om;
                customerNpvSum += netCashFlow / Math.pow(1 + discountRate, yr);

                if (customerPayback === null && cumulative >= 0) {
                    // Linear interpolation for fractional payback year
                    const prevCum = cumulative - netCashFlow;
                    customerPayback = yr - 1 + Math.abs(prevCum) / netCashFlow;
                }

                annualCashFlows.push({
                    year: yr,
                    savings,
                    vppRevenue: vppRev,
                    om,
                    derCharges,
                    fuelCost,
                    netCashFlow,
                    cumulative
                });
            }

            const lifetimeNetValue = lifetimeSavings + lifetimeVpp - lifetimeOM - salePrice + customerItc;

            // Compute customer IRR via Newton-Raphson
            let customerIrr = null;
            const irrCashFlows = [year0CashFlow, ...annualCashFlows.map(cf => cf.netCashFlow)];
            let rate = 0.10;
            for (let iter = 0; iter < 100; iter++) {
                let npvVal = 0;
                let dnpv = 0;
                for (let t = 0; t < irrCashFlows.length; t++) {
                    npvVal += irrCashFlows[t] / Math.pow(1 + rate, t);
                    dnpv -= t * irrCashFlows[t] / Math.pow(1 + rate, t + 1);
                }
                if (Math.abs(dnpv) < 1e-10) break;
                const newRate = rate - npvVal / dnpv;
                if (Math.abs(newRate - rate) < 1e-6) {
                    customerIrr = newRate;
                    break;
                }
                rate = newRate;
                if (rate < -0.99 || rate > 10) break;
            }

            return {
                salePrice,
                markup: capexMargin,
                customerPayback: customerPayback > 0 ? parseFloat(customerPayback.toFixed(1)) : null,
                customerNpv: customerNpvSum,
                customerItc,
                term,
                annualOM,
                annualCashFlows,
                year0CashFlow,
                lifetimeSavings,
                lifetimeVpp,
                lifetimeOM,
                lifetimeNetValue,
                customerIrr
            };
        } catch (error) {
            console.error('CapEx calculation error:', error);
            return null;
        }
    }, [systemCost, capexConfig, itcCalculations, annualSavings, operatingCosts, discountRate, vppAnnualRevenue, vppDurationYears, monthlyPerformanceData]);

    // Prepare chart data with cumulative cash flow
    const cashFlowChartData = useMemo(() => {
        // CapEx mode: customer cumulative cash flow
        if (viewMode === 'capex' && capexResults?.annualCashFlows) {
            const data = [{
                year: 0,
                netCashFlow: capexResults.year0CashFlow,
                cumulativeCashFlow: capexResults.year0CashFlow
            }];
            capexResults.annualCashFlows.forEach(cf => {
                data.push({
                    year: cf.year,
                    netCashFlow: cf.netCashFlow,
                    cumulativeCashFlow: cf.cumulative
                });
            });
            return data;
        }

        // ESA mode: developer cumulative cash flow
        if (!esaResults?.annualCashFlows || !systemCost) return null;

        const initialCashFlow = -systemCost + itcAmount;
        let cumulative = initialCashFlow;

        const data = [{
            year: 0,
            netCashFlow: initialCashFlow,
            cumulativeCashFlow: cumulative
        }];

        esaResults.annualCashFlows.forEach((netCashFlow, idx) => {
            cumulative += netCashFlow;
            data.push({
                year: idx + 1,
                netCashFlow: netCashFlow,
                cumulativeCashFlow: cumulative
            });
        });

        return data;
    }, [viewMode, capexResults, esaResults?.annualCashFlows, systemCost, itcAmount]);

    // Calculate payback year for chart reference line
    const paybackYear = useMemo(() => {
        if (!cashFlowChartData) return null;
        const paybackPoint = cashFlowChartData.find(d => d.cumulativeCashFlow >= 0 && d.year > 0);
        return paybackPoint?.year || null;
    }, [cashFlowChartData]);

    // Handle ESA config changes
    const handleEsaConfigChange = useCallback((field, value) => {
        setEsaConfig(prev => ({ ...prev, [field]: value }));
    }, []);

    // Handle CapEx config changes
    const handleCapexConfigChange = useCallback((field, value) => {
        setCapexConfig(prev => ({ ...prev, [field]: value }));
    }, []);

    // Store onConfigChange in a ref to avoid dependency issues
    const onConfigChangeRef = React.useRef(onConfigChange);
    React.useEffect(() => {
        onConfigChangeRef.current = onConfigChange;
    }, [onConfigChange]);

    // Auto-save when config changes (debounced)
    // Skip initial render to avoid saving on mount
    const isInitialMount = React.useRef(true);
    React.useEffect(() => {
        // Skip initial mount
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        if (!onConfigChangeRef.current) return;

        // Debounce the save to avoid saving on every keystroke
        const timeoutId = setTimeout(() => {
            // Extract computed values to save for comparison modal
            const computedResults = esaResults ? {
                // Developer economics - with ITC
                irrWithItc: esaResults.developerReturns?.withItc?.irr ?? null,
                developerNpvWithItc: esaResults.developerReturns?.withItc?.npv ?? null,
                paybackYearsWithItc: esaResults.developerReturns?.withItc?.paybackYears ?? null,
                // Developer economics - without ITC
                irrWithoutItc: esaResults.developerReturns?.withoutItc?.irr ?? null,
                developerNpvWithoutItc: esaResults.developerReturns?.withoutItc?.npv ?? null,
                paybackYearsWithoutItc: esaResults.developerReturns?.withoutItc?.paybackYears ?? null,
                // CapEx and ITC
                totalCapex: systemCost ?? null,
                itcAmount: itcAmount ?? null,
                netCapexAfterItc: systemCost && itcAmount ? systemCost - itcAmount : null,
                // OpEx
                year1OpEx: esaResults.expenses?.annual?.[0]?.total ?? null,
                totalExpenses: esaResults.expenses?.totalExpenses ?? null,
                // Revenue
                totalRevenue: esaResults.revenue?.totalRevenue ?? null,
                year1EsaPayment: esaResults.revenue?.annual?.[0]?.revenue ?? null,
                // Annual data for detailed view
                annualRevenue: esaResults.revenue?.annual ?? null,
                annualExpenses: esaResults.expenses?.annual ?? null,
                annualCashFlows: esaResults.annualCashFlows ?? null,
                // Customer economics
                year1CustomerSavings: esaResults.customerValue?.year1Savings ?? null,
                lifetimeCustomerSavings: esaResults.customerValue?.lifetimeSavings ?? null,
                customerNpv: esaResults.customerValue?.npvOfSavings ?? null,
                effectiveRate: esaResults.customerValue?.effectiveRate ?? null,
                rateDiscount: esaResults.customerValue?.rateDiscount ?? null,
                // Customer annual cash flows
                customerAnnualCashflows: esaResults.customerAnnualCashflows ?? null,
                // VPP revenue (if enabled)
                vppAnnualRevenue: vppAnnualRevenue > 0 ? vppAnnualRevenue : null,
                lifetimeVppRevenue: esaLifetimeVpp > 0 ? esaLifetimeVpp : null,
                // Raw VPP estimate (always saved so shared view can toggle VPP on/off)
                vppEstimateAnnual: (() => {
                    if (!vppEstimate) return null;
                    const low = vppEstimate.adjTotalAnnualLow || 0;
                    const high = vppEstimate.adjTotalAnnualHigh || 0;
                    return (low + high) / 2 || null;
                })(),
                // Full VPP detail for shared view breakdown
                vppDetail: vppEstimate?.estimate ? {
                    iso: vppEstimate.estimate.iso,
                    zone: vppEstimate.estimate.zone,
                    revenueStreams: vppEstimate.estimate.revenue_streams,
                    drPrograms: vppEstimate.estimate.dr_programs?.filter(p => p.eligible && p.annual_est > 0),
                    cannibalization: vppEstimate.estimate.cannibalization,
                    participationMode: vppEstimate.participationMode,
                    aggregatorCutPct: vppEstimate.aggregatorCutPct,
                    adjTotalAnnualLow: vppEstimate.adjTotalAnnualLow,
                    adjTotalAnnualHigh: vppEstimate.adjTotalAnnualHigh,
                    grossLow: vppEstimate.estimate.total_annual_low,
                    grossHigh: vppEstimate.estimate.total_annual_high,
                } : null,
                // Legacy compatibility
                irr: esaResults.developerReturns?.withItc?.irr ?? null,
                developerNpv: esaResults.developerReturns?.withItc?.npv ?? null,
                paybackYears: esaResults.developerReturns?.withItc?.paybackYears ?? null,
            } : null;

            onConfigChangeRef.current({
                viewMode,
                esaConfig,
                capexConfig,
                discountRate,
                // VPP settings
                vppIncluded: includeVpp,
                vppDurationYears,
                // Save computed results for comparison modal
                computedResults
            });
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [viewMode, esaConfig, capexConfig, discountRate, esaResults, includeVpp, vppDurationYears]);

    // Legacy handleBlur for any remaining onBlur handlers (no-op now, save is automatic)
    const handleBlur = useCallback(() => {
        // Auto-save is now handled by useEffect above
    }, []);

    // Export handler - generates and downloads CSV file
    const handleExportCsv = useCallback(() => {
        const csv = generateDealAnalysisCSV(
            viewMode,
            esaConfig,
            capexConfig,
            esaResults,
            capexResults,
            budget,
            systemSizes
        );
        const filename = `deal-analysis-${viewMode}-${new Date().toISOString().split('T')[0]}.csv`;
        downloadFile(csv, filename);
    }, [viewMode, esaConfig, capexConfig, esaResults, capexResults, budget, systemSizes]);

    // Render Key Metrics Card
    const renderKeyMetrics = (title, metrics, icon, bgColor) => (
        <Card sx={{
            height: '100%',
            backgroundColor: bgColor,
            ...(isRetro && RETRO_CARD_SX)
        }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    {icon}
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 600,
                            ...(isRetro && RETRO_FONT_SX)
                        }}
                    >
                        {title}
                    </Typography>
                </Box>
                <Grid container spacing={2}>
                    {metrics.map((metric, idx) => (
                        <Grid item xs={6} key={idx}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={isRetro ? RETRO_FONT_SX : {}}
                            >
                                {metric.label}
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    color: metric.color || 'text.primary',
                                    ...(isRetro && RETRO_FONT_SX)
                                }}
                            >
                                {metric.value}
                            </Typography>
                        </Grid>
                    ))}
                </Grid>
            </CardContent>
        </Card>
    );

    // Render ESA Configuration Section
    const renderEsaConfig = () => (
        <Card sx={{
            mb: 3,
            ...(isRetro && RETRO_CARD_SX)
        }}>
            <CardContent>
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        ...(isRetro && RETRO_FONT_SX)
                    }}
                >
                    <Handshake sx={{ color: vortxColors.gold }} />
                    ESA Configuration
                </Typography>

                <Grid container spacing={3}>
                    {/* Pricing Model */}
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel>Pricing Model</InputLabel>
                            <Select
                                value={esaConfig.model}
                                label="Pricing Model"
                                onChange={(e) => handleEsaConfigChange('model', e.target.value)}
                                onBlur={handleBlur}
                            >
                                {Object.entries(PRICING_MODEL_LABELS).map(([key, label]) => (
                                    <MenuItem
                                        key={key}
                                        value={key}
                                        disabled={key === 'fixed_rate' && !hasProductionData}
                                    >
                                        {label}
                                        {key === 'fixed_rate' && !hasProductionData && ' (requires solar)'}
                                    </MenuItem>
                                ))}
                            </Select>
                            {!hasProductionData && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                    $/kWh pricing requires solar production data
                                </Typography>
                            )}
                        </FormControl>
                    </Grid>

                    {/* Term */}
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Contract Term (years)"
                            type="number"
                            value={esaConfig.term}
                            onChange={(e) => handleEsaConfigChange('term', parseInt(e.target.value) || 20)}
                            onBlur={handleBlur}
                            inputProps={{ min: 1, max: 30 }}
                        />
                    </Grid>

                    {/* Escalation */}
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Annual Escalation"
                            type="number"
                            value={Number((esaConfig.escalation * 100).toFixed(1))}
                            onChange={(e) => handleEsaConfigChange('escalation', parseFloat(e.target.value) / 100 || 0)}
                            onBlur={handleBlur}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>
                            }}
                            inputProps={{ step: 0.1 }}
                        />
                    </Grid>

                    {/* Model-specific inputs */}
                    {esaConfig.model === 'fixed_rate' && (
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Rate"
                                type="number"
                                value={esaConfig.rate}
                                onChange={(e) => handleEsaConfigChange('rate', parseFloat(e.target.value) || 0)}
                                onBlur={handleBlur}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    endAdornment: <InputAdornment position="end">/kWh</InputAdornment>
                                }}
                                inputProps={{ step: 0.01 }}
                            />
                        </Grid>
                    )}

                    {esaConfig.model === 'savings_share' && (
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Savings Share"
                                type="number"
                                value={Number((esaConfig.sharePercent * 100).toFixed(0))}
                                onChange={(e) => handleEsaConfigChange('sharePercent', parseFloat(e.target.value) / 100 || 0)}
                                onBlur={handleBlur}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>
                                }}
                                inputProps={{ min: 0, max: 100, step: 5 }}
                            />
                        </Grid>
                    )}

                    {esaConfig.model === 'fixed_monthly' && (
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Yearly Payment"
                                type="number"
                                value={Math.round(esaConfig.payment * 12)}
                                onChange={(e) => handleEsaConfigChange('payment', (parseFloat(e.target.value) || 0) / 12)}
                                onBlur={handleBlur}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    endAdornment: <InputAdornment position="end">/year</InputAdornment>
                                }}
                                inputProps={{ step: 1000 }}
                            />
                        </Grid>
                    )}

                    {esaConfig.model === 'capacity_payment' && (
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Capacity Rate"
                                type="number"
                                value={esaConfig.ratePerKw}
                                onChange={(e) => handleEsaConfigChange('ratePerKw', parseFloat(e.target.value) || 0)}
                                onBlur={handleBlur}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    endAdornment: <InputAdornment position="end">/kW/mo</InputAdornment>
                                }}
                                inputProps={{ step: 1 }}
                            />
                        </Grid>
                    )}

                    {/* Discount Rate */}
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Discount Rate"
                            type="number"
                            value={Number((discountRate * 100).toFixed(1))}
                            onChange={(e) => setDiscountRate(parseFloat(e.target.value) / 100 || 0.08)}
                            onBlur={handleBlur}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>
                            }}
                            inputProps={{ step: 0.5 }}
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );

    // Render CapEx Configuration Section
    const renderCapexConfig = () => (
        <Card sx={{
            mb: 3,
            ...(isRetro && RETRO_CARD_SX)
        }}>
            <CardContent>
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        ...(isRetro && RETRO_FONT_SX)
                    }}
                >
                    <Store sx={{ color: vortxColors.teal }} />
                    CapEx Sale Configuration
                </Typography>

                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={6} md={3}>
                        <TextField
                            fullWidth
                            label="Markup"
                            type="number"
                            value={capexConfig.markupPercent}
                            onChange={(e) => handleCapexConfigChange('markupPercent', parseFloat(e.target.value) || 0)}
                            onBlur={handleBlur}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>
                            }}
                            inputProps={{ min: 0, max: 100, step: 5 }}
                        />
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <TextField
                            fullWidth
                            label="Escalation Rate"
                            type="number"
                            value={Number((capexConfig.escalationRate * 100).toFixed(1))}
                            onChange={(e) => handleCapexConfigChange('escalationRate', parseFloat(e.target.value) / 100 || 0)}
                            onBlur={handleBlur}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>
                            }}
                            inputProps={{ min: 0, max: 10, step: 0.5 }}
                        />
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <TextField
                            fullWidth
                            label="Term"
                            type="number"
                            value={capexConfig.term}
                            onChange={(e) => handleCapexConfigChange('term', parseInt(e.target.value) || 25)}
                            onBlur={handleBlur}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">yrs</InputAdornment>
                            }}
                            inputProps={{ min: 5, max: 35, step: 5 }}
                        />
                    </Grid>

                    <Grid item xs={6} md={2}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={capexConfig.customerItcEligible}
                                    onChange={(e) => handleCapexConfigChange('customerItcEligible', e.target.checked)}
                                    onBlur={handleBlur}
                                />
                            }
                            label="Customer ITC"
                        />
                        <Tooltip title="If customer can claim Investment Tax Credit on their purchase">
                            <IconButton size="small">
                                <Info fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Sale Price</Typography>
                            <Typography variant="h5" sx={{ color: vortxColors.gold, fontWeight: 700 }}>
                                {formatCurrency(capexResults?.salePrice || 0)}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );

    // Render ESA Key Metrics
    const renderEsaMetrics = () => {
        if (!esaResults) return null;

        const developerMetrics = [
            {
                label: 'IRR (with ITC)',
                value: formatPercent(esaResults.developerReturns?.withItc?.irr, 2),
                color: vortxColors.gold
            },
            {
                label: 'Payback Period',
                value: esaResults.developerReturns?.withItc?.paybackYears
                    ? `${esaResults.developerReturns.withItc.paybackYears.toFixed(1)} years`
                    : 'N/A'
            },
            {
                label: 'Total Revenue',
                value: formatCurrency((esaResults.revenue?.totalRevenue || 0) + esaLifetimeVpp)
            },
            ...(esaLifetimeVpp > 0 ? [{
                label: 'incl. VPP',
                value: formatCurrency(esaLifetimeVpp),
                color: vortxColors.gold
            }] : [])
        ];

        const customerMetrics = [
            {
                label: 'Year 1 Savings',
                value: formatCurrency(esaResults.customerValue?.year1Savings),
                color: (esaResults.customerValue?.year1Savings || 0) >= 0 ? 'success.main' : 'error.main'
            },
            {
                label: 'Lifetime Savings',
                value: formatCurrency(esaResults.customerValue?.lifetimeSavings),
                color: (esaResults.customerValue?.lifetimeSavings || 0) >= 0 ? 'success.main' : 'error.main'
            },
            {
                label: 'NPV of Savings',
                value: formatCurrency(esaResults.customerValue?.npvOfSavings)
            },
            {
                label: 'Rate Discount',
                value: formatPercent(esaResults.customerValue?.rateDiscount, 1)
            }
        ];

        return (
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                    {renderKeyMetrics(
                        'Developer Returns',
                        developerMetrics,
                        <TrendingUp sx={{ color: vortxColors.gold }} />,
                        isDark ? 'rgba(178, 140, 80, 0.1)' : 'rgba(178, 140, 80, 0.05)'
                    )}
                </Grid>
                <Grid item xs={12} md={6}>
                    {renderKeyMetrics(
                        'Customer Value',
                        customerMetrics,
                        <AccountBalance sx={{ color: vortxColors.teal }} />,
                        isDark ? 'rgba(63, 106, 100, 0.1)' : 'rgba(63, 106, 100, 0.05)'
                    )}
                </Grid>
            </Grid>
        );
    };

    // Render CapEx Key Metrics
    const renderCapexMetrics = () => {
        if (!capexResults) return null;

        const developerMetrics = [
            {
                label: 'Gross Margin',
                value: formatCurrency(capexResults.markup?.grossMargin),
                color: vortxColors.gold
            },
            {
                label: 'Gross Margin %',
                value: formatPercent(capexResults.markup?.grossMarginPercent, 1)
            },
            {
                label: 'Net Margin (w/ ITC)',
                value: formatCurrency(capexResults.markup?.netMargin),
                color: 'success.main'
            },
            {
                label: 'Developer ITC',
                value: formatCurrency(capexResults.markup?.developerItc)
            }
        ];

        const customerMetrics = [
            {
                label: 'Sale Price',
                value: formatCurrency(capexResults.salePrice),
                color: vortxColors.teal
            },
            {
                label: 'Customer ITC',
                value: capexResults.customerItc > 0 ? formatCurrency(capexResults.customerItc) : 'N/A'
            },
            {
                label: 'Payback Period',
                value: capexResults.customerPayback
                    ? `${capexResults.customerPayback} years`
                    : 'N/A'
            },
            {
                label: 'NPV',
                value: formatCurrency(capexResults.customerNpv),
                color: (capexResults.customerNpv || 0) >= 0 ? 'success.main' : 'error.main'
            }
        ];

        return (
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                    {renderKeyMetrics(
                        'Developer Returns',
                        developerMetrics,
                        <TrendingUp sx={{ color: vortxColors.gold }} />,
                        isDark ? 'rgba(178, 140, 80, 0.1)' : 'rgba(178, 140, 80, 0.05)'
                    )}
                </Grid>
                <Grid item xs={12} md={6}>
                    {renderKeyMetrics(
                        'Customer Value',
                        customerMetrics,
                        <AccountBalance sx={{ color: vortxColors.teal }} />,
                        isDark ? 'rgba(63, 106, 100, 0.1)' : 'rgba(63, 106, 100, 0.05)'
                    )}
                </Grid>
            </Grid>
        );
    };

    // Render Cash Flow Chart
    const renderCashFlowChart = () => {
        if (!cashFlowChartData) return null;

        // Custom tooltip formatter
        const CustomTooltip = ({ active, payload, label }) => {
            if (active && payload && payload.length) {
                return (
                    <Box
                        sx={{
                            backgroundColor: isDark ? '#1e1e1e' : '#fff',
                            border: `1px solid ${isDark ? '#444' : '#ccc'}`,
                            borderRadius: 1,
                            p: 1.5,
                            boxShadow: 2
                        }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Year {label}
                        </Typography>
                        <Typography variant="body2" sx={{ color: vortxColors.gold }}>
                            Cumulative: {formatCurrency(payload[0]?.value)}
                        </Typography>
                    </Box>
                );
            }
            return null;
        };

        return (
            <Card sx={{
                mb: 3,
                ...(isRetro && RETRO_CARD_SX)
            }}>
                <CardContent>
                    <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            ...(isRetro && RETRO_FONT_SX)
                        }}
                    >
                        <TrendingUp sx={{ color: vortxColors.gold }} />
                        {viewMode === 'capex' ? 'Customer Cumulative Cash Flow' : 'Cumulative Cash Flow'}
                    </Typography>
                    <Box sx={{ height: 300 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                                data={cashFlowChartData}
                                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                            >
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke={isDark ? '#444' : '#ccc'}
                                />
                                <XAxis
                                    dataKey="year"
                                    label={{ value: 'Year', position: 'insideBottom', offset: -5 }}
                                    stroke={isDark ? '#aaa' : '#666'}
                                />
                                <YAxis
                                    tickFormatter={(value) => formatCurrency(value)}
                                    stroke={isDark ? '#aaa' : '#666'}
                                />
                                <RechartsTooltip content={<CustomTooltip />} />
                                {/* Break-even reference line at $0 */}
                                <ReferenceLine
                                    y={0}
                                    stroke={isDark ? '#888' : '#666'}
                                    strokeDasharray="5 5"
                                    label={{
                                        value: 'Break-even',
                                        position: 'right',
                                        fill: isDark ? '#aaa' : '#666'
                                    }}
                                />
                                {/* Payback year reference line */}
                                {paybackYear && (
                                    <ReferenceLine
                                        x={paybackYear}
                                        stroke={vortxColors.teal}
                                        strokeDasharray="5 5"
                                        label={{
                                            value: `Payback (Year ${paybackYear})`,
                                            position: 'top',
                                            fill: vortxColors.teal
                                        }}
                                    />
                                )}
                                <Line
                                    type="monotone"
                                    dataKey="cumulativeCashFlow"
                                    stroke={vortxColors.gold}
                                    strokeWidth={2}
                                    dot={{ fill: vortxColors.gold, strokeWidth: 2, r: 4 }}
                                    activeDot={{ r: 6, fill: vortxColors.gold }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </Box>
                </CardContent>
            </Card>
        );
    };

    // Render Comparison Table
    const renderComparisonTable = () => {
        if (!esaResults || !capexResults) {
            return (
                <Alert severity="info" sx={{ mb: 3 }}>
                    Configure both ESA and CapEx options to see comparison.
                </Alert>
            );
        }

        const comparisonRows = [
            {
                category: 'Developer Returns',
                metrics: [
                    {
                        label: 'IRR',
                        esa: formatPercent(esaResults.developerReturns?.withItc?.irr, 2),
                        capex: 'N/A (One-time sale)'
                    },
                    {
                        label: 'NPV',
                        esa: formatCurrency(esaResults.developerReturns?.withItc?.npv),
                        capex: formatCurrency(capexResults.markup?.netMargin)
                    },
                    {
                        label: 'Payback Period',
                        esa: esaResults.developerReturns?.withItc?.paybackYears
                            ? `${esaResults.developerReturns.withItc.paybackYears.toFixed(1)} years`
                            : 'N/A',
                        capex: 'Immediate'
                    },
                    {
                        label: 'Total Revenue/Margin',
                        esa: formatCurrency((esaResults.revenue?.totalRevenue || 0) + (esaLifetimeVpp || 0)),
                        capex: formatCurrency(capexResults.markup?.grossMargin)
                    }
                ]
            },
            {
                category: 'Customer Value',
                metrics: [
                    {
                        label: 'Upfront Cost',
                        esa: '$0',
                        capex: formatCurrency(capexResults.salePrice - (capexResults.customerItc || 0))
                    },
                    {
                        label: 'Year 1 Net',
                        esa: formatCurrency(esaResults.customerValue?.year1Savings),
                        capex: formatCurrency(annualSavings + (vppAnnualRevenue > 0 ? vppAnnualRevenue : 0))
                    },
                    {
                        label: 'Lifetime Value',
                        esa: formatCurrency(esaResults.customerValue?.lifetimeSavings),
                        capex: formatCurrency(capexResults.customerNpv)
                    },
                    {
                        label: 'Ownership',
                        esa: 'Developer owns system',
                        capex: 'Customer owns system'
                    }
                ]
            }
        ];

        return (
            <Card sx={{
                mb: 3,
                ...(isRetro && RETRO_CARD_SX)
            }}>
                <CardContent>
                    <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            ...(isRetro && RETRO_FONT_SX)
                        }}
                    >
                        <Compare sx={{ color: vortxColors.gold }} />
                        ESA vs CapEx Comparison
                    </Typography>

                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Metric</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, color: vortxColors.gold }}>
                                        ESA
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, color: vortxColors.teal }}>
                                        CapEx Sale
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {comparisonRows.map((section) => (
                                    <React.Fragment key={section.category}>
                                        <TableRow sx={{ backgroundColor: isDark ? 'rgba(100, 150, 200, 0.1)' : '#f0f8ff' }}>
                                            <TableCell colSpan={3}>
                                                <strong>{section.category}</strong>
                                            </TableCell>
                                        </TableRow>
                                        {section.metrics.map((metric, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell sx={{ pl: 3 }}>{metric.label}</TableCell>
                                                <TableCell align="right">{metric.esa}</TableCell>
                                                <TableCell align="right">{metric.capex}</TableCell>
                                            </TableRow>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        );
    };

    // Render Annual Cash Flows Table
    const renderAnnualCashFlows = () => {
        // ESA mode: developer cash flows
        if ((viewMode === 'esa' || viewMode === 'compare') && esaResults?.revenue?.annual) {
            return (
                <Accordion expanded={cashFlowsExpanded} onChange={() => setCashFlowsExpanded(!cashFlowsExpanded)} sx={{ mb: 3 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AttachMoney />
                            Annual Cash Flows (Developer)
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Year</TableCell>
                                        <TableCell align="right">ESA Revenue</TableCell>
                                        {vppAnnualRevenue > 0 && <TableCell align="right">VPP Revenue</TableCell>}
                                        <TableCell align="right">Expenses</TableCell>
                                        <TableCell align="right">Net Cash Flow</TableCell>
                                        <TableCell align="right">Cumulative</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {esaResults.revenue.annual.map((rev, idx) => {
                                        const exp = esaResults.expenses?.annual?.[idx];
                                        const netCashFlow = esaResults.annualCashFlows?.[idx] || 0;
                                        const initialInvestment = itcAmount - systemCost;
                                        const cumulative = esaResults.annualCashFlows
                                            ?.slice(0, idx + 1)
                                            .reduce((a, b) => a + b, initialInvestment);
                                        const year = idx + 1;
                                        const vppRev = (vppAnnualRevenue > 0 && year <= vppDurationYears)
                                            ? vppAnnualRevenue * Math.pow(1.03, idx)
                                            : 0;

                                        return (
                                            <TableRow key={idx}>
                                                <TableCell>{rev.year}</TableCell>
                                                <TableCell align="right">{formatCurrency(rev.revenue)}</TableCell>
                                                {vppAnnualRevenue > 0 && (
                                                    <TableCell align="right" sx={{ color: vppRev > 0 ? vortxColors.gold : vortxColors.stone }}>
                                                        {vppRev > 0 ? formatCurrency(vppRev) : '\u2014'}
                                                    </TableCell>
                                                )}
                                                <TableCell align="right">{formatCurrency(exp?.total || 0)}</TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={{ color: netCashFlow >= 0 ? 'success.main' : 'error.main' }}
                                                >
                                                    {formatCurrency(netCashFlow)}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={{ color: cumulative >= 0 ? 'success.main' : 'error.main' }}
                                                >
                                                    {formatCurrency(cumulative)}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </AccordionDetails>
                </Accordion>
            );
        }

        // CapEx mode: customer cash flows
        if (viewMode === 'capex' && capexResults?.annualCashFlows) {
            const capexHasDerCharges = capexResults.annualCashFlows[0]?.derCharges > 0;
            const capexHasFuelCosts = capexResults.annualCashFlows[0]?.fuelCost > 0;
            const capexHasVpp = capexResults.annualCashFlows.some(cf => cf.vppRevenue > 0);
            return (
                <Accordion expanded={cashFlowsExpanded} onChange={() => setCashFlowsExpanded(!cashFlowsExpanded)} sx={{ mb: 3 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AttachMoney />
                            Customer Annual Cash Flows ({capexConfig.term || 25} years)
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{ overflowX: 'auto' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Year</TableCell>
                                        <TableCell align="right">Utility Savings</TableCell>
                                        {capexHasVpp && <TableCell align="right">VPP Revenue</TableCell>}
                                        <TableCell align="right">O&M</TableCell>
                                        {capexHasDerCharges && <TableCell align="right">DER Charges</TableCell>}
                                        {capexHasFuelCosts && <TableCell align="right">Fuel Cost</TableCell>}
                                        <TableCell align="right">Net Cash Flow</TableCell>
                                        <TableCell align="right">Cumulative</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {capexResults.annualCashFlows.map((cf, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell>{cf.year}</TableCell>
                                            <TableCell align="right" sx={{ color: vortxColors.teal }}>
                                                {formatCurrency(cf.savings)}
                                            </TableCell>
                                            {capexHasVpp && (
                                                <TableCell align="right" sx={{ color: cf.vppRevenue > 0 ? vortxColors.gold : vortxColors.stone }}>
                                                    {cf.vppRevenue > 0 ? formatCurrency(cf.vppRevenue) : '\u2014'}
                                                </TableCell>
                                            )}
                                            <TableCell align="right" sx={{ color: vortxColors.stone }}>
                                                ({formatCurrency(cf.om)})
                                            </TableCell>
                                            {capexHasDerCharges && (
                                                <TableCell align="right" sx={{ color: '#ff9800' }}>
                                                    ({formatCurrency(cf.derCharges)})
                                                </TableCell>
                                            )}
                                            {capexHasFuelCosts && (
                                                <TableCell align="right" sx={{ color: '#ff9800' }}>
                                                    ({formatCurrency(cf.fuelCost)})
                                                </TableCell>
                                            )}
                                            <TableCell
                                                align="right"
                                                sx={{ color: cf.netCashFlow >= 0 ? 'success.main' : 'error.main' }}
                                            >
                                                {formatCurrency(cf.netCashFlow)}
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                sx={{ color: cf.cumulative >= 0 ? 'success.main' : 'error.main' }}
                                            >
                                                {formatCurrency(cf.cumulative)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </AccordionDetails>
                </Accordion>
            );
        }

        return null;
    };

    return (
        <Box>
            {/* Quick Guarantee Score — shows risk grade + savings bands */}
            {(guaranteeScore || guaranteeLoading) && (
                <Box sx={{ mb: 3 }}>
                    <GuaranteeScoreCard
                        scoreData={guaranteeScore}
                        loading={guaranteeLoading}
                        annualSavingsDollars={annualSavings}
                        baselineCost={guaranteeScore?.baseline_cost}
                        optimizedCost={guaranteeScore?.optimized_cost}
                    />
                </Box>
            )}

            {/* Header with View Mode Toggle and Export Buttons */}
            <Card sx={{
                mb: 3,
                ...(isRetro && RETRO_CARD_SX)
            }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                        <Grid item>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 700,
                                    color: vortxColors.gold,
                                    ...(isRetro && RETRO_FONT_SX)
                                }}
                            >
                                Deal Analysis
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={isRetro ? RETRO_FONT_SX : {}}
                            >
                                Model ESA vs CapEx deals and compare developer returns
                            </Typography>
                        </Grid>

                        <Grid item>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <ToggleButtonGroup
                                    value={viewMode}
                                    exclusive
                                    onChange={(e, newMode) => newMode && setViewMode(newMode)}
                                    size="small"
                                    sx={isRetro ? {
                                        '& .MuiToggleButton-root': {
                                            ...RETRO_FONT_SX,
                                            borderRadius: 0,
                                            border: `1px solid ${RETRO_COLORS.teal}`,
                                            color: RETRO_COLORS.teal,
                                            '&.Mui-selected': {
                                                backgroundColor: 'rgba(95, 212, 196, 0.2)',
                                                color: RETRO_COLORS.teal,
                                            },
                                        },
                                    } : {}}
                                >
                                    <ToggleButton value="esa">
                                        <Handshake sx={{ mr: 1 }} /> ESA
                                    </ToggleButton>
                                    <ToggleButton value="capex">
                                        <Store sx={{ mr: 1 }} /> CapEx
                                    </ToggleButton>
                                    <ToggleButton value="compare">
                                        <Compare sx={{ mr: 1 }} /> Compare
                                    </ToggleButton>
                                </ToggleButtonGroup>

                                <Divider orientation="vertical" flexItem />

                                <Box>
                                    <Tooltip title="Export to CSV">
                                        <IconButton size="small" onClick={handleExportCsv}>
                                            <Download fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Performance Guarantee */}
            <Accordion sx={{ mb: 3 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ShieldIcon sx={{ color: vortxColors.teal }} />
                        <Typography variant="h6">Performance Guarantee</Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <GuaranteeSimulationSection
                        projectId={projectId}
                        scenarioId={scenarioId}
                        optimization={optimization}
                        systemSizes={systemSizes}
                    />
                </AccordionDetails>
            </Accordion>

            {/* VPP Revenue Integration */}
            {vppEstimate && (vppEstimate.adjTotalAnnualLow > 0 || vppEstimate.adjTotalAnnualHigh > 0) && (
                <Card sx={{ mb: 3, ...(isRetro && RETRO_CARD_SX) }}>
                    <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={includeVpp}
                                            onChange={(e) => setIncludeVpp(e.target.checked)}
                                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: vortxColors.gold }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: vortxColors.gold } }}
                                        />
                                    }
                                    label={
                                        <Typography variant="body2" sx={{ fontWeight: 600, ...(isRetro && RETRO_FONT_SX) }}>
                                            Include VPP Revenue
                                        </Typography>
                                    }
                                />
                                {includeVpp && (
                                    <Chip
                                        label={`${formatCurrency(vppAnnualRevenue)}/yr (midpoint)`}
                                        size="small"
                                        sx={{ backgroundColor: vortxColors.gold + '30', color: vortxColors.gold, fontWeight: 600 }}
                                    />
                                )}
                            </Box>
                            {includeVpp && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Typography variant="body2" color="text.secondary" sx={isRetro ? RETRO_FONT_SX : {}}>
                                        Assume available for:
                                    </Typography>
                                    <Select
                                        value={vppDurationYears}
                                        onChange={(e) => setVppDurationYears(e.target.value)}
                                        size="small"
                                        sx={{ minWidth: 120, ...(isRetro && { ...RETRO_FONT_SX, borderRadius: 0 }) }}
                                    >
                                        <MenuItem value={5}>5 years</MenuItem>
                                        <MenuItem value={10}>10 years</MenuItem>
                                        <MenuItem value={15}>15 years</MenuItem>
                                        <MenuItem value={20}>20 years</MenuItem>
                                        <MenuItem value={99}>Full term</MenuItem>
                                    </Select>
                                </Box>
                            )}
                        </Box>
                        {includeVpp && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, ...(isRetro && RETRO_FONT_SX) }}>
                                Range: {formatCurrency(vppEstimate.adjTotalAnnualLow)} {'\u2013'} {formatCurrency(vppEstimate.adjTotalAnnualHigh)}/yr
                                ({vppEstimate.participationMode === 'direct' ? 'Self-Managed' : `Aggregator, ${100 - vppEstimate.aggregatorCutPct}% retained`}).
                                Revenue escalated at energy rate. {vppDurationYears < 99 ? `Drops to $0 after year ${vppDurationYears}.` : ''}
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Collapsible Budget Section */}
            <Accordion expanded={budgetExpanded} onChange={() => setBudgetExpanded(!budgetExpanded)} sx={{ mb: 3 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Typography variant="h6">Budget (CapEx)</Typography>
                        <Chip
                            label={budgetLoading ? 'Loading...' : formatCurrency(systemCost)}
                            size="small"
                            sx={{ backgroundColor: vortxColors.gold + '30', color: vortxColors.gold }}
                        />
                        <Chip
                            label={budgetLoading ? 'Loading...' : `Net: ${formatCurrency(netCostAfterItc)}`}
                            size="small"
                            color="success"
                            variant="outlined"
                        />
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <BudgetTab
                        systemSizes={systemSizes}
                        budget={budget}
                        hasSystemConfigured={true}
                        readOnly={false}
                        {...budgetTabProps}
                    />
                </AccordionDetails>
            </Accordion>

            {/* Configuration Sections based on View Mode */}
            {(viewMode === 'esa' || viewMode === 'compare') && renderEsaConfig()}
            {(viewMode === 'capex' || viewMode === 'compare') && renderCapexConfig()}

            {/* Key Metrics based on View Mode */}
            {viewMode === 'esa' && renderEsaMetrics()}
            {viewMode === 'capex' && renderCapexMetrics()}
            {viewMode === 'compare' && (
                <>
                    {renderComparisonTable()}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: vortxColors.gold }}>
                                ESA Summary
                            </Typography>
                            {renderEsaMetrics()}
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: vortxColors.teal }}>
                                CapEx Summary
                            </Typography>
                            {renderCapexMetrics()}
                        </Grid>
                    </Grid>
                </>
            )}

            {/* Cash Flow Chart */}
            {renderCashFlowChart()}

            {/* Annual Cash Flows */}
            {renderAnnualCashFlows()}

            {/* CapEx Customer Value Breakdown */}
            {viewMode === 'capex' && capexResults?.annualCashFlows && (
                <Card sx={{
                    mb: 3,
                    ...(isRetro && RETRO_CARD_SX)
                }}>
                    <CardContent>
                        <Typography
                            variant="h6"
                            gutterBottom
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                ...(isRetro && RETRO_FONT_SX)
                            }}
                        >
                            <AccountBalance sx={{ color: vortxColors.teal }} />
                            Customer Value Breakdown
                        </Typography>

                        <Grid container spacing={3} sx={{ mb: 3 }}>
                            <Grid item xs={6} md={capexResults.lifetimeVpp > 0 ? 2.4 : 3}>
                                <Box sx={{ textAlign: 'center', p: 2, borderRadius: 1, backgroundColor: isDark ? 'rgba(63, 106, 100, 0.1)' : 'rgba(63, 106, 100, 0.05)' }}>
                                    <Typography variant="caption" color="text.secondary">Lifetime Savings</Typography>
                                    <Typography variant="h6" sx={{ color: vortxColors.teal, fontWeight: 700 }}>
                                        {formatCurrency(capexResults.lifetimeSavings)}
                                    </Typography>
                                </Box>
                            </Grid>
                            {capexResults.lifetimeVpp > 0 && (
                                <Grid item xs={6} md={2.4}>
                                    <Box sx={{ textAlign: 'center', p: 2, borderRadius: 1, backgroundColor: isDark ? 'rgba(178, 140, 80, 0.15)' : 'rgba(178, 140, 80, 0.08)' }}>
                                        <Typography variant="caption" color="text.secondary">VPP Revenue</Typography>
                                        <Typography variant="h6" sx={{ color: vortxColors.gold, fontWeight: 700 }}>
                                            {formatCurrency(capexResults.lifetimeVpp)}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                            <Grid item xs={6} md={capexResults.lifetimeVpp > 0 ? 2.4 : 3}>
                                <Box sx={{ textAlign: 'center', p: 2, borderRadius: 1, backgroundColor: isDark ? 'rgba(178, 140, 80, 0.1)' : 'rgba(178, 140, 80, 0.05)' }}>
                                    <Typography variant="caption" color="text.secondary">Total O&M</Typography>
                                    <Typography variant="h6" sx={{ color: vortxColors.stone, fontWeight: 700 }}>
                                        ({formatCurrency(capexResults.lifetimeOM)})
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6} md={capexResults.lifetimeVpp > 0 ? 2.4 : 3}>
                                <Box sx={{ textAlign: 'center', p: 2, borderRadius: 1, backgroundColor: isDark ? 'rgba(63, 106, 100, 0.15)' : 'rgba(63, 106, 100, 0.08)' }}>
                                    <Typography variant="caption" color="text.secondary">Net Lifetime Value</Typography>
                                    <Typography variant="h6" sx={{ color: capexResults.lifetimeNetValue >= 0 ? 'success.main' : 'error.main', fontWeight: 700 }}>
                                        {formatCurrency(capexResults.lifetimeNetValue)}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6} md={capexResults.lifetimeVpp > 0 ? 2.4 : 3}>
                                <Box sx={{ textAlign: 'center', p: 2, borderRadius: 1, backgroundColor: isDark ? 'rgba(178, 140, 80, 0.1)' : 'rgba(178, 140, 80, 0.05)' }}>
                                    <Typography variant="caption" color="text.secondary">IRR</Typography>
                                    <Typography variant="h6" sx={{ color: vortxColors.gold, fontWeight: 700 }}>
                                        {capexResults.customerIrr != null ? `${(capexResults.customerIrr * 100).toFixed(1)}%` : '—'}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* ESA Customer Value Breakdown */}
            {(viewMode === 'esa' || viewMode === 'compare') && esaResults?.customerValue && (
                <Card sx={{
                    mb: 3,
                    ...(isRetro && RETRO_CARD_SX)
                }}>
                    <CardContent>
                        <Typography
                            variant="h6"
                            gutterBottom
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                ...(isRetro && RETRO_FONT_SX)
                            }}
                        >
                            <AccountBalance sx={{ color: vortxColors.teal }} />
                            Customer Value Breakdown
                        </Typography>
                        {/* Year 1 Customer Economics */}
                        {(() => {
                            const year1 = esaResults.customerAnnualCashflows?.[0];
                            const hasDerCharges = (year1?.derCharges || 0) > 0;
                            const hasFuelCosts = (year1?.fuelCost || 0) > 0;
                            // Determine grid sizing based on number of items
                            const itemCount = 3 + (hasDerCharges ? 1 : 0) + (hasFuelCosts ? 1 : 0);
                            const gridSize = itemCount <= 3 ? 4 : itemCount === 4 ? 3 : 2.4;

                            return (
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                        Year 1 Customer Economics
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={gridSize}>
                                            <Box sx={{
                                                p: 2,
                                                backgroundColor: isDark ? 'rgba(63, 106, 100, 0.15)' : 'rgba(63, 106, 100, 0.08)',
                                                borderRadius: 1,
                                                textAlign: 'center'
                                            }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Utility Savings
                                                </Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 600, color: vortxColors.teal }}>
                                                    {formatCurrency(year1?.utilitySavings || annualSavings)}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={gridSize}>
                                            <Box sx={{
                                                p: 2,
                                                backgroundColor: isDark ? 'rgba(100, 100, 100, 0.15)' : 'rgba(100, 100, 100, 0.08)',
                                                borderRadius: 1,
                                                textAlign: 'center'
                                            }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    ESA Payment
                                                </Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 600, color: vortxColors.stone }}>
                                                    ({formatCurrency(year1?.esaPayment || 0)})
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        {hasDerCharges && (
                                            <Grid item xs={12} sm={gridSize}>
                                                <Box sx={{
                                                    p: 2,
                                                    backgroundColor: isDark ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.08)',
                                                    borderRadius: 1,
                                                    textAlign: 'center'
                                                }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        DER Charges
                                                    </Typography>
                                                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#ff9800' }}>
                                                        ({formatCurrency(year1?.derCharges || 0)})
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )}
                                        {hasFuelCosts && (
                                            <Grid item xs={12} sm={gridSize}>
                                                <Box sx={{
                                                    p: 2,
                                                    backgroundColor: isDark ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.08)',
                                                    borderRadius: 1,
                                                    textAlign: 'center'
                                                }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Fuel Cost
                                                    </Typography>
                                                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#ff9800' }}>
                                                        ({formatCurrency(year1?.fuelCost || 0)})
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )}
                                        <Grid item xs={12} sm={gridSize}>
                                            <Box sx={{
                                                p: 2,
                                                backgroundColor: isDark ? 'rgba(178, 140, 80, 0.15)' : 'rgba(178, 140, 80, 0.08)',
                                                borderRadius: 1,
                                                textAlign: 'center'
                                            }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Net Cashflow
                                                </Typography>
                                                <Typography variant="h6" sx={{
                                                    fontWeight: 600,
                                                    color: (year1?.netCashflow || 0) >= 0 ? vortxColors.gold : 'error.main'
                                                }}>
                                                    {formatCurrency(year1?.netCashflow || 0)}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            );
                        })()}

                        {/* Customer Annual Cash Flows Accordion */}
                        {(() => {
                            // Check if there are DER charges or fuel costs to display
                            const hasDerCharges = esaResults.customerAnnualCashflows?.[0]?.derCharges > 0;
                            const hasFuelCosts = esaResults.customerAnnualCashflows?.[0]?.fuelCost > 0;

                            return (
                                <Accordion sx={{ mb: 3 }}>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography variant="subtitle2">
                                            Customer Annual Cash Flows ({esaConfig.term} years)
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box sx={{ overflowX: 'auto' }}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Year</TableCell>
                                                        <TableCell align="right">Utility Savings</TableCell>
                                                        <TableCell align="right">ESA Payment</TableCell>
                                                        {hasDerCharges && <TableCell align="right">DER Charges</TableCell>}
                                                        {hasFuelCosts && <TableCell align="right">Fuel Cost</TableCell>}
                                                        <TableCell align="right">Net Cashflow</TableCell>
                                                        <TableCell align="right">Cumulative</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {esaResults.customerAnnualCashflows?.map((cf, idx) => {
                                                        const cumulative = esaResults.customerAnnualCashflows
                                                            ?.slice(0, idx + 1)
                                                            .reduce((a, b) => a + b.netCashflow, 0);

                                                        return (
                                                            <TableRow key={idx}>
                                                                <TableCell>{cf.year}</TableCell>
                                                                <TableCell align="right" sx={{ color: vortxColors.teal }}>
                                                                    {formatCurrency(cf.utilitySavings)}
                                                                </TableCell>
                                                                <TableCell align="right" sx={{ color: vortxColors.stone }}>
                                                                    ({formatCurrency(cf.esaPayment)})
                                                                </TableCell>
                                                                {hasDerCharges && (
                                                                    <TableCell align="right" sx={{ color: '#ff9800' }}>
                                                                        ({formatCurrency(cf.derCharges)})
                                                                    </TableCell>
                                                                )}
                                                                {hasFuelCosts && (
                                                                    <TableCell align="right" sx={{ color: '#ff9800' }}>
                                                                        ({formatCurrency(cf.fuelCost)})
                                                                    </TableCell>
                                                                )}
                                                                <TableCell
                                                                    align="right"
                                                                    sx={{ color: cf.netCashflow >= 0 ? vortxColors.gold : 'error.main' }}
                                                                >
                                                                    {formatCurrency(cf.netCashflow)}
                                                                </TableCell>
                                                                <TableCell
                                                                    align="right"
                                                                    sx={{ color: cumulative >= 0 ? 'success.main' : 'error.main' }}
                                                                >
                                                                    {formatCurrency(cumulative)}
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })()}

                        <Divider sx={{ my: 2 }} />

                        {/* Lifetime Value Chart */}
                        {(() => {
                            // Calculate totals from customer annual cashflows
                            const lifetimeSavings = esaResults.customerAnnualCashflows?.reduce((sum, cf) => sum + cf.utilitySavings, 0) || 0;
                            const lifetimeEsaPayments = esaResults.revenue?.totalRevenue || 0;
                            const lifetimeDerCharges = esaResults.customerAnnualCashflows?.reduce((sum, cf) => sum + (cf.derCharges || 0), 0) || 0;
                            const lifetimeFuelCosts = esaResults.customerAnnualCashflows?.reduce((sum, cf) => sum + (cf.fuelCost || 0), 0) || 0;
                            const netLifetimeValue = esaResults.customerAnnualCashflows?.reduce((sum, cf) => sum + cf.netCashflow, 0) || 0;

                            // Build chart data dynamically based on what costs exist
                            const chartData = [
                                { name: 'Utility Savings', value: lifetimeSavings, displayValue: lifetimeSavings, color: vortxColors.teal }
                            ];

                            chartData.push({ name: 'ESA Payments', value: lifetimeEsaPayments, displayValue: -lifetimeEsaPayments, color: vortxColors.stone });

                            if (lifetimeDerCharges > 0) {
                                chartData.push({ name: 'DER Charges', value: lifetimeDerCharges, displayValue: -lifetimeDerCharges, color: '#ff9800' });
                            }

                            if (lifetimeFuelCosts > 0) {
                                chartData.push({ name: 'Fuel Costs', value: lifetimeFuelCosts, displayValue: -lifetimeFuelCosts, color: '#ff9800' });
                            }

                            chartData.push({ name: 'Net Value', value: netLifetimeValue, displayValue: netLifetimeValue, color: vortxColors.gold });

                            const chartHeight = 80 + chartData.length * 35;

                            return (
                                <>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                        Lifetime Value ({esaConfig.term} years)
                                    </Typography>
                                    <Grid container spacing={3} alignItems="center">
                                        <Grid item xs={12} md={8}>
                                            <Box sx={{ height: chartHeight }}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart
                                                        layout="vertical"
                                                        data={chartData}
                                                        margin={{ top: 10, right: 30, left: 100, bottom: 10 }}
                                                    >
                                                        <CartesianGrid
                                                            strokeDasharray="3 3"
                                                            stroke={isDark ? '#444' : '#ccc'}
                                                            horizontal={false}
                                                        />
                                                        <XAxis
                                                            type="number"
                                                            tickFormatter={(value) => formatCurrency(value)}
                                                            stroke={isDark ? '#aaa' : '#666'}
                                                        />
                                                        <YAxis
                                                            type="category"
                                                            dataKey="name"
                                                            stroke={isDark ? '#aaa' : '#666'}
                                                            width={90}
                                                        />
                                                        <RechartsTooltip
                                                            formatter={(value, name, props) => [
                                                                formatCurrency(props.payload.displayValue),
                                                                props.payload.name
                                                            ]}
                                                            contentStyle={{
                                                                backgroundColor: isDark ? '#1e1e1e' : '#fff',
                                                                border: `1px solid ${isDark ? '#444' : '#ccc'}`,
                                                                borderRadius: 4
                                                            }}
                                                        />
                                                        <ReferenceLine x={0} stroke={isDark ? '#888' : '#666'} />
                                                        <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                                                            {chartData.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                                            ))}
                                                        </Bar>
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Box sx={{ textAlign: 'center', p: 2 }}>
                                                {/* Show different metrics based on pricing model */}
                                                {esaResults.customerValue?.pricingModel === 'capacity_payment' ? (
                                                    <>
                                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                            Yearly Payment
                                                        </Typography>
                                                        <Typography
                                                            variant="h4"
                                                            sx={{ fontWeight: 700, color: vortxColors.gold, mb: 1 }}
                                                        >
                                                            {formatCurrency(esaResults.revenue?.annual?.[0]?.revenue || 0)}/yr
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            ${esaConfig.ratePerKw}/kW/mo × {totalCapacityKw.toFixed(0)} kW × 12 mo
                                                        </Typography>
                                                    </>
                                                ) : !hasProductionData ? (
                                                    <>
                                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                            Annual Payment
                                                        </Typography>
                                                        <Typography
                                                            variant="h4"
                                                            sx={{ fontWeight: 700, color: vortxColors.gold, mb: 1 }}
                                                        >
                                                            {formatCurrency(esaResults.revenue?.annual?.[0]?.revenue || 0)}/yr
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {esaConfig.model === 'savings_share'
                                                                ? `${(esaConfig.sharePercent * 100).toFixed(0)}% of ${formatCurrency(annualSavings)} savings`
                                                                : `${formatCurrency(esaConfig.payment)}/mo fixed fee`
                                                            }
                                                        </Typography>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                            Effective Rate
                                                        </Typography>
                                                        <Typography
                                                            variant="h4"
                                                            sx={{ fontWeight: 700, color: vortxColors.gold, mb: 1 }}
                                                        >
                                                            ${(esaResults.customerValue?.effectiveRate || 0).toFixed(3)}/kWh
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            vs ${bauUtilityRate.toFixed(3)}/kWh utility
                                                        </Typography>
                                                    </>
                                                )}
                                                {esaResults.customerValue?.rateDiscount > 0 ? (
                                                    <Box sx={{ mt: 2, p: 1.5, backgroundColor: isDark ? 'rgba(63, 106, 100, 0.2)' : 'rgba(63, 106, 100, 0.1)', borderRadius: 1 }}>
                                                        <Typography variant="h6" sx={{ color: vortxColors.teal, fontWeight: 600 }}>
                                                            {formatPercent(esaResults.customerValue?.rateDiscount, 1)} Savings
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            vs current utility rate
                                                        </Typography>
                                                    </Box>
                                                ) : !hasProductionData && esaResults.customerValue?.year1Savings > 0 && (
                                                    <Box sx={{ mt: 2, p: 1.5, backgroundColor: isDark ? 'rgba(63, 106, 100, 0.2)' : 'rgba(63, 106, 100, 0.1)', borderRadius: 1 }}>
                                                        <Typography variant="h6" sx={{ color: vortxColors.teal, fontWeight: 600 }}>
                                                            {formatCurrency(esaResults.customerValue.year1Savings)}/yr
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Year 1 net customer savings
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </>
                            );
                        })()}
                    </CardContent>
                </Card>
            )}

            {/* No data warning - only show when budget is loaded but systemCost is 0 */}
            {!budgetLoading && !systemCost && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    Configure your system and budget to see deal analysis calculations.
                </Alert>
            )}
        </Box>
    );
}

export default DealAnalysisTab;
